import {
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import styled, { css } from "styled-components/macro";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING } from "constants/customers";
import { useIsDarkMode } from "store/atoms";
import { darkModeTextColor } from "constants/colors";

const TagInputRowStyles = styled.div<{
  $disabled: boolean;
  $isDarkMode: boolean;
}>`
  display: grid;
  grid-template-columns: 20em 1fr 6em auto;
  width: max-content;
  margin: 0 auto;
  align-items: center;
  text-align: left;

  .tagName {
    margin-right: 1em;
    color: ${props => (props.$disabled ? "rgba(0, 0, 0, 0.38)" : "black")};
    ${({ $isDarkMode, $disabled }) =>
      $isDarkMode
        ? css`
            color: ${$disabled
              ? "rgba(255, 255, 255, 0.38)"
              : darkModeTextColor};
          `
        : ""}
  }

  .textField {
    display: flex;
    flex-direction: row;
    align-items: center;

    /* Hide original up/down arrows */
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input[type="number"] {
      -moz-appearance: textfield;
    }

    .numMinutes {
      width: 6em;
      font-size: 2em;
    }

    .upDownButtons {
      display: flex;
      flex-direction: column;
      padding: 0;
      width: 0px;

      button {
        width: 10px;
        transform: translateX(-100%) translateY(4px);
      }

      .MuiButton-text,
      .MuiButton-root {
        padding: 0;
      }
    }
  }

  /* X Close button */
  .deleteRow {
    margin-left: 2em !important;
    color: red;
  }
`;

type TagInputRowProps = {
  name: string;
  value: number;
  handleOnChange: (e: any) => void;
  disabled: boolean;
  handleArrowClick: (name: string, shouldIncrease: boolean) => void;
  disableIncrease: boolean;
  disableDecrease: boolean;
  removeTagMinuteInput: () => void;
  checked: boolean;
  handleCheckClick: (e: any) => void;
};

export default function TagInputRow({
  name,
  value,
  handleOnChange,
  disabled,
  handleArrowClick,
  disableIncrease,
  disableDecrease,
  removeTagMinuteInput,
  checked,
  handleCheckClick,
}: TagInputRowProps) {
  const [isDarkMode] = useIsDarkMode();
  const ckey = useCustomerKey();

  // If customer does not use changeovers, we want to hide C/O checkbox
  const usesChangeovers =
    CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING?.[ckey].usesChangeovers;

  const handleIncrease = () => {
    handleArrowClick(name, true);
  };
  const handleDecrease = () => {
    handleArrowClick(name, false);
  };

  return (
    <TagInputRowStyles $disabled={disabled} $isDarkMode={isDarkMode}>
      <div className="tagName">{name}</div>
      <div className="textField">
        <TextField
          className="numMinutes"
          name={name}
          label="Minutes"
          type="number"
          margin="normal"
          variant={(disabled ? "standard" : "outlined") as any}
          value={value}
          onChange={handleOnChange}
          disabled={disabled}
          inputProps={{
            min: "0",
            step: "1",
          }}
        />
        <div className="upDownButtons">
          <Button
            onClick={handleIncrease}
            disabled={disableIncrease || disabled}
            className="addButton"
          >
            <KeyboardArrowUpIcon />
          </Button>
          <Button
            onClick={handleDecrease}
            disabled={disableDecrease || disabled}
            className="subButton"
          >
            <KeyboardArrowDownIcon />
          </Button>
        </div>
      </div>
      {usesChangeovers && (
        <FormControlLabel
          className="changeoverCheckbox"
          value="start"
          control={
            <Checkbox
              color="primary"
              checked={checked}
              onChange={handleCheckClick}
              disabled={disabled}
              name="changeoverCheck"
            />
          }
          label="C/O"
          labelPlacement="start"
        />
      )}
      <IconButton
        onClick={removeTagMinuteInput}
        disabled={disabled}
        className="deleteRow"
        size="large"
      >
        <CloseIcon />
      </IconButton>
    </TagInputRowStyles>
  );
}
